import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { ListFilters } from '../../../interfaces/user.interface';
import { buildHeadCells } from '../../../utils/methods';
import {
  GENERIC_LOOKUP_KEYS,
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
} from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import UserService from '../../../services/user.service';
import RoleService from '../../../services/role.service';
import CommonService from '../../../services/common.service';
import { SortOrder, Modules, AuthRoutes } from '../../../enums';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    searchText: '',
    role: '',
    status: '1',
    stateId: '',
    cityId: [],
    areaUserIds: [],
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_users', defaultFilters);

  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    role: '',
    status: '',
    state: '',
    city: '',
    area: '',
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.first_name,
      VALIDATION_FIELD_MAPPING.phoneNumber,
      VALIDATION_FIELD_MAPPING.companyName,
      VALIDATION_FIELD_MAPPING.role,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'userList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.first_name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: AuthRoutes.users,
    uri: Modules.users + AuthRoutes.users,
    perPage,
    page,
    order,
    orderBy,
  });

  const {
    total,
    data: users,
    loading,
  } = useFetch({
    service: UserService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.userList[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      role: filters.role,
      status: filters.status === '1' ? 1 : filters.status === '0' ? 0 : '',
      stateId: filters.stateId,
      cityId: filters.cityId,
      areaUserIds: filters.areaUserIds,
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.role,
      filters.status,
      filters.stateId,
      filters.cityId,
      filters.areaUserIds,
    ],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: '',
      countryId: '1', // Defaulting to India
    },
    dependency: [],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: dropdownSearchData.state || '',
      countryId: '1',
    },
    dependency: [dropdownSearchData.state],
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: '',
      stateId: filters.stateId,
    },
    dependency: [filters.stateId],
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: dropdownSearchData.city || '',
      stateId: filters.stateId,
    },
    dependency: [dropdownSearchData.city, filters.stateId],
  });

  const { data: allAreas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: '',
      cityId: filters.cityId,
    },
    dependency: [filters.cityId],
  });

  const { data: areas } = useFetch({
    service: CommonService,
    func: 'areaLookup',
    payload: {
      searchText: dropdownSearchData.area || '',
      cityId: filters.cityId,
    },
    dependency: [dropdownSearchData.area, filters.cityId],
  });

  const { data: kpis } = useFetch({
    service: UserService,
    func: 'kpis',
    payload: null,
    dependency: [],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const { data: allRolesLookup } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: {
      searchText: '',
    },
    dependency: [],
  });



  const { data: roles } = useFetch({
    service: RoleService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.role },
    dependency: [dropdownSearchData.role],
  });



  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.first_name);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage, setOrder, setOrderBy]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.first_name);
    setFilters(defaultFilters);
    resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFilters, setPage, setOrder, setOrderBy]);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const values: TableContextProvider = useMemo(() => {
    const headerSection = <TableHeaderSection total={total} kpis={kpis} />;
    return {
      headerSection,
      row: Row,
      data: users || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('users.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    t,
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    kpis,
    loading,
    users,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
  ]);

  return (
    <>
      <Header />

      <Stats data={kpis} />

      <Filters
        allRoles={allRolesLookup}
        initialFilters={initialFilters}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        roles={roles}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        status={status}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        states={states}
        allStates={allStates}
        cities={cities}
        allCities={allCities}
        areas={areas}
        allAreas={allAreas}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default List;
